import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const DAY_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function Stars({ n }) {
  return (
    <span>
      {[1,2,3,4,5].map(i=>(
        <span key={i} style={{color: i<=n ? '#f59e0b' : '#ddd', fontSize:'1rem'}}>&#9733;</span>
      ))}
    </span>
  );
}

function BookingModal({ tutor, user, onClose }) {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [levels, setLevels] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [subjectId, setSubjectId] = useState('');
  const [levelId, setLevelId] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [needTopup, setNeedTopup] = useState(false);
  const [week, setWeek] = useState(0);

  const [bookedSlots, setBookedSlots] = useState([]);

  const isBooked = (date, slot) => {
    const dateStr = date.toISOString().split('T')[0];
    const timeStr = slot.start_time.slice(0,5);
    return bookedSlots.some(b => {
      const bd = new Date(b);
      return bd.toISOString().slice(0,16) === (dateStr + 'T' + timeStr).slice(0,16);
    });
  };

  useEffect(() => {
    api.get('/search/tutor/' + tutor.slug).then(r => {
      const t = r.data.tutor;
      setSubjects(t?.subjects || []);
      setLevels(t?.levels || []);
      setAvailability(t?.availability || []);
      setBookedSlots(t?.bookedSlots || []);
    });
  }, []);

  const price = user?.currency === 'USD' ? '10' : '8';
  const sym = user?.currency === 'USD' ? '$' : 'GBP ';

  const getWeekDates = () => {
    const dates = [];
    const today = new Date();
    today.setDate(today.getDate() + week * 7);
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const weekDates = getWeekDates();

  const getSlotsForDay = (dayOfWeek) => {
    return availability.filter(a => a.day_of_week === dayOfWeek);
  };

  const formatSlotTime = (timeStr) => {
    return timeStr ? timeStr.slice(0,5) : '';
  };

  const handleSlotClick = (date, slot) => {
    const dateStr = date.toISOString().split('T')[0];
    const timeStr = slot.start_time.slice(0,5);
    setSelectedSlot({ date: dateStr, time: timeStr, display: DAYS[date.getDay()] + ' ' + date.toLocaleDateString('en-GB', {day:'numeric',month:'short'}) + ' at ' + timeStr });
  };

  const handleBook = async () => {
    if (!subjectId || !levelId || !selectedSlot) return setError('Please select a subject, level and time slot');
    setLoading(true); setError('');
    try {
      const scheduledAt = selectedSlot.date + 'T' + selectedSlot.time + ':00';
      await api.post('/bookings', {
        tutor_id: tutor.id,
        subject_id: parseInt(subjectId),
        level_id: parseInt(levelId),
        booking_type: 'one_off',
        sessions: [{ scheduled_at: scheduledAt }],
        parent_notes: notes
      });
      setSuccess(true);
      setTimeout(() => { onClose(); navigate('/parent/bookings'); }, 2000);
    } catch (err) {
      const msg = err.response?.data?.message || 'Booking failed';
      if (msg.toLowerCase().includes('insufficient') || msg.toLowerCase().includes('wallet')) {
        setError(msg + ' — please top up your wallet first.');
        setNeedTopup(true);
      } else {
        setError(msg);
      }
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:560,maxHeight:'90vh',overflowY:'auto'}}>
        <button className="modal-close" onClick={onClose} style={{position:'sticky',top:0,float:'right'}}>X</button>
        <h2 className="modal-title">Book a lesson</h2>
        <p style={{fontSize:'0.875rem',color:'var(--ink-400)',marginBottom:20}}>with {tutor.full_name} - {sym}{price}/hr</p>

        {success ? (
          <div className="alert alert-success">Booking sent! Awaiting tutor confirmation. Redirecting...</div>
        ) : (
          <>
            {error && (
              <div className="alert alert-error" style={{marginBottom:16}}>
                {error}
                {needTopup && (
                  <div style={{marginTop:10}}>
                    <button className="btn btn-primary btn-sm" onClick={()=>{ window.open('/parent/wallet', '_blank'); }}>
                      Top up wallet (opens in new tab)
                    </button>
                  </div>
                )}
              </div>
            )}

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Subject</label>
                <select className="form-select" value={subjectId} onChange={e=>setSubjectId(e.target.value)}>
                  <option value="">Select subject</option>
                  {subjects.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Level</label>
                <select className="form-select" value={levelId} onChange={e=>setLevelId(e.target.value)}>
                  <option value="">Select level</option>
                  {levels.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Pick a slot</label>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={()=>setWeek(w=>w-1)} disabled={week<=0}>Previous week</button>
                <span style={{fontSize:'0.82rem',fontWeight:600,color:'var(--ink-400)'}}>
                  {weekDates[0].toLocaleDateString('en-GB',{day:'numeric',month:'short'})} - {weekDates[6].toLocaleDateString('en-GB',{day:'numeric',month:'short'})}
                </span>
                <button type="button" className="btn btn-ghost btn-sm" onClick={()=>setWeek(w=>w+1)}>Next week</button>
              </div>

              {availability.length === 0 ? (
                <div style={{textAlign:'center',padding:20,color:'var(--ink-400)',background:'var(--ink-50)',borderRadius:10}}>
                  <p style={{margin:0}}>This tutor has not set their availability yet.</p>
                </div>
              ) : (
                <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4}}>
                  {weekDates.map((date, i) => {
                    const slots = getSlotsForDay(date.getDay());
                    const isPast = date < new Date();
                    return (
                      <div key={i} style={{textAlign:'center'}}>
                        <div style={{fontSize:'0.7rem',fontWeight:700,color:'var(--ink-400)',marginBottom:4}}>{DAY_SHORT[date.getDay()]}</div>
                        <div style={{fontSize:'0.7rem',color:'var(--ink-300)',marginBottom:6}}>{date.getDate()}</div>
                        {slots.length === 0 || isPast ? (
                          <div style={{height:32,background:'var(--ink-50)',borderRadius:6,opacity:0.4}}/>
                        ) : (
                          slots.map((slot, j) => {
                            const isSelected = !isBooked(date,slot) && selectedSlot?.date === date.toISOString().split('T')[0] && selectedSlot?.time === slot.start_time.slice(0,5);
                            return (
                              <button key={j} type="button"
                                onClick={()=>!isBooked(date,slot) && handleSlotClick(date,slot)}
                                disabled={isBooked(date,slot)}
                                title={isBooked(date,slot) ? 'Already booked' : ''}
                                style={{
                                  width:'100%',padding:'4px 2px',marginBottom:3,borderRadius:6,border:'none',
                                  fontSize:'0.65rem',fontWeight:700,
                                  cursor: isBooked(date,slot) ? 'not-allowed' : 'pointer',
                                  background: isBooked(date,slot) ? '#fee2e2' : isSelected ? '#22c97f' : '#edfaf3',
                                  color: isBooked(date,slot) ? '#991b1b' : isSelected ? '#fff' : '#0a4a35',
                                  boxShadow: isSelected ? '0 2px 8px rgba(34,201,127,0.4)' : 'none',
                                  textDecoration: isBooked(date,slot) ? 'line-through' : 'none',
                                  opacity: isBooked(date,slot) ? 0.6 : 1
                                }}>
                                {formatSlotTime(slot.start_time)}
                              </button>
                            );
                          })
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedSlot && (
                <div style={{marginTop:12,padding:'10px 14px',background:'var(--green-50)',borderRadius:8,border:'1.5px solid var(--green-300)',fontSize:'0.875rem',fontWeight:600,color:'var(--green-800)'}}>
                  Selected: {selectedSlot.display}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Notes for tutor (optional)</label>
              <textarea className="form-textarea" rows={3} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="e.g. My child is preparing for GCSE exams in May..."/>
            </div>

            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleBook} disabled={loading||!selectedSlot||!subjectId||!levelId}>
                {loading ? 'Booking...' : 'Confirm - ' + sym + price}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function TutorProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');
  const [showBooking, setShowBooking] = useState(false);

  useEffect(() => {
    api.get('/search/tutor/' + slug)
      .then(r => setTutor(r.data.tutor))
      .catch(() => navigate('/find-tutors'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="loading-screen"><div className="spinner spinner-lg"/></div>;
  if (!tutor) return null;

  const initials = tutor.full_name?.split(' ').map(w=>w[0]).join('').toUpperCase();
  const rating = parseFloat(tutor.average_rating || 0);
  const price = user?.currency === 'USD' ? '10' : '8';
  const sym = user?.currency === 'USD' ? '$' : 'GBP ';

  return (
    <div className="page-wrapper">
      <Navbar />
      <div style={{background:'linear-gradient(135deg,#052e1c,#0a4a35)',padding:'36px 0'}}>
        <div className="container">
          <div style={{display:'flex',gap:24,alignItems:'flex-start',flexWrap:'wrap'}}>
            {tutor.profile_photo_url
              ? <img src={tutor.profile_photo_url} alt={tutor.full_name} style={{width:96,height:96,borderRadius:'50%',objectFit:'cover',border:'3px solid #22c97f'}}/>
              : <div style={{width:96,height:96,borderRadius:'50%',background:'linear-gradient(135deg,#22c97f,#0d6e4f)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.8rem',fontWeight:800,color:'#fff',flexShrink:0}}>{initials}</div>
            }
            <div style={{flex:1}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6,flexWrap:'wrap'}}>
                <h1 style={{color:'#fff',fontSize:'1.6rem',margin:0}}>{tutor.full_name}</h1>
                {tutor.has_dbs && <span className="badge badge-dbs">DBS checked</span>}
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                <Stars n={Math.round(rating)}/>
                <span style={{color:'rgba(255,255,255,0.7)',fontSize:'0.85rem'}}>
                  {rating.toFixed(1)} - {tutor.total_reviews} reviews - {tutor.total_lessons} lessons
                </span>
              </div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {tutor.subjects?.slice(0,4).map(s=>(
                  <span key={s.id} style={{background:'rgba(255,255,255,0.12)',color:'rgba(255,255,255,0.9)',padding:'4px 12px',borderRadius:14,fontSize:'0.78rem',fontWeight:600}}>{s.name}</span>
                ))}
              </div>
            </div>
            <div style={{background:'rgba(255,255,255,0.08)',borderRadius:16,padding:'20px 24px',minWidth:200,border:'1.5px solid rgba(255,255,255,0.12)',flexShrink:0}}>
              <div style={{color:'#22c97f',fontSize:'1.8rem',fontWeight:800,lineHeight:1}}>{sym}{price}<span style={{fontSize:'1rem',color:'rgba(255,255,255,0.6)',fontWeight:500}}>/hr</span></div>
              <div style={{color:'rgba(255,255,255,0.5)',fontSize:'0.8rem',marginBottom:14}}>Fixed rate</div>
              {user?.role === 'parent'
                ? <button className="btn btn-primary btn-full" onClick={()=>setShowBooking(true)}>Book a lesson</button>
                : user ? null
                : <Link to={"/register/parent?redirect=" + encodeURIComponent(window.location.pathname)} className="btn btn-primary btn-full">Sign up to book</Link>
              }
            </div>
          </div>
        </div>
      </div>

      <div style={{borderBottom:'1.5px solid var(--ink-100)',background:'#fff',position:'sticky',top:68,zIndex:10}}>
        <div className="container" style={{display:'flex',gap:0}}>
          {['about','reviews','availability'].map(tab=>(
            <button key={tab} onClick={()=>setActiveTab(tab)} style={{
              padding:'16px 20px',fontWeight:700,fontSize:'0.875rem',border:'none',background:'none',cursor:'pointer',
              color: activeTab===tab ? 'var(--green-700)' : 'var(--ink-400)',
              borderBottom: activeTab===tab ? '2.5px solid var(--green-500)' : '2.5px solid transparent',
              textTransform:'capitalize'
            }}>{tab}</button>
          ))}
        </div>
      </div>

      <div className="container" style={{padding:'32px 24px'}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:28}}>
          <div>
            {activeTab === 'about' && (
              <div>

                {tutor.intro_video_url && (
                  <div style={{marginBottom:24}}>
                    <h3 style={{marginBottom:12}}>Introduction video</h3>
                    <video controls src={tutor.intro_video_url} style={{width:'100%',borderRadius:12,maxHeight:360,background:'#000',display:'block'}}
                      poster="" preload="metadata"/>
                    <p style={{fontSize:'0.78rem',color:'var(--ink-400)',marginTop:6}}>Watch this tutor introduce themselves</p>
                  </div>
                )}
                {tutor.bio && (
                  <div className="card" style={{marginBottom:20}}>
                    <h3 style={{marginBottom:12}}>About me</h3>
                    <p style={{margin:0,lineHeight:1.8}}>{tutor.bio}</p>
                  </div>
                )}
                {tutor.qualifications && (
                  <div className="card" style={{marginBottom:20}}>
                    <h3 style={{marginBottom:12}}>Qualifications</h3>
                    <p style={{margin:0,lineHeight:1.8,whiteSpace:'pre-line'}}>{tutor.qualifications}</p>
                  </div>
                )}
                {tutor.work_experience && tutor.work_experience.length > 0 && (
                  <div className="card" style={{marginBottom:20}}>
                    <h3 style={{marginBottom:16}}>Work experience</h3>
                    {tutor.work_experience.map((w,i)=>(
                      <div key={i} style={{padding:'12px 16px',background:'var(--ink-50)',borderRadius:10,marginBottom:10,borderLeft:'3px solid var(--green-400)'}}>
                        <div style={{fontWeight:700,fontSize:'0.95rem'}}>{w.role}</div>
                        <div style={{fontWeight:600,color:'var(--green-700)',fontSize:'0.875rem'}}>{w.employer}</div>
                        <div style={{fontSize:'0.78rem',color:'var(--ink-400)',margin:'3px 0'}}>
                          {w.from} - {w.current ? 'Present' : w.to}
                        </div>
                        {w.description && <p style={{margin:'6px 0 0',fontSize:'0.875rem',color:'var(--ink-500)'}}>{w.description}</p>}
                      </div>
                    ))}
                  </div>
                )}
                <div className="card" style={{marginBottom:20}}>
                  <h3 style={{marginBottom:16}}>Subjects and levels</h3>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                    <div>
                      <p style={{fontWeight:700,fontSize:'0.8rem',color:'var(--ink-400)',textTransform:'uppercase',marginBottom:8}}>Subjects</p>
                      <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                        {tutor.subjects?.map(s=><span key={s.id} className="badge badge-green">{s.name}</span>)}
                      </div>
                    </div>
                    <div>
                      <p style={{fontWeight:700,fontSize:'0.8rem',color:'var(--ink-400)',textTransform:'uppercase',marginBottom:8}}>Levels</p>
                      <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                        {tutor.levels?.map(l=><span key={l.id} className="badge badge-blue">{l.name}</span>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <div style={{padding:20,background:'var(--green-50)',borderRadius:12,border:'1.5px solid var(--green-200)',marginBottom:20}}>
                  <div style={{fontSize:'2.5rem',fontWeight:800,color:'var(--ink)',lineHeight:1}}>{rating.toFixed(1)}</div>
                  <Stars n={Math.round(rating)}/>
                  <div style={{fontSize:'0.78rem',color:'var(--ink-400)',marginTop:4}}>{tutor.total_reviews} reviews</div>
                </div>
                {!tutor.reviews?.length && <p style={{color:'var(--ink-400)'}}>No reviews yet.</p>}
                {tutor.reviews?.map((r,i)=>(
                  <div key={i} className="card" style={{marginBottom:12}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
                      <strong style={{fontSize:'0.9rem'}}>{r.parent_name}</strong>
                      <Stars n={r.rating}/>
                    </div>
                    {r.review_text && <p style={{margin:0,fontSize:'0.875rem',color:'var(--ink-500)'}}>{r.review_text}</p>}
                    {r.tutor_reply && (
                      <div style={{marginTop:12,padding:'10px 14px',background:'var(--green-50)',borderRadius:8,borderLeft:'3px solid var(--green-400)'}}>
                        <p style={{margin:0,fontSize:'0.8rem',color:'var(--green-800)'}}>Tutor replied: {r.tutor_reply}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'availability' && (
              <div className="card">
                <h3 style={{marginBottom:16}}>Weekly availability</h3>
                {!tutor.availability?.length
                  ? <p style={{color:'var(--ink-400)'}}>No availability set.</p>
                  : <div style={{display:'flex',flexDirection:'column',gap:8}}>
                    {DAY_SHORT.map((day,i)=>{
                      const slots = tutor.availability?.filter(a=>a.day_of_week===i)||[];
                      return slots.length ? (
                        <div key={i} style={{display:'flex',gap:12,alignItems:'center',padding:'10px 14px',background:'var(--green-50)',borderRadius:8}}>
                          <span style={{fontWeight:700,width:36,fontSize:'0.85rem',color:'var(--green-800)'}}>{day}</span>
                          <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                            {slots.map((s,j)=>(
                              <span key={j} style={{background:'var(--green-500)',color:'#fff',borderRadius:6,padding:'3px 10px',fontSize:'0.78rem',fontWeight:600}}>
                                {s.start_time.slice(0,5)}-{s.end_time.slice(0,5)}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })}
                  </div>
                }
              </div>
            )}
          </div>

          <div>
            <div className="card card-elevated" style={{position:'sticky',top:120,padding:24}}>
              <div style={{fontSize:'1.6rem',fontWeight:800,color:'var(--green-800)',marginBottom:4}}>{sym}{price}<span style={{fontSize:'0.9rem',color:'var(--ink-400)',fontWeight:500}}>/hr</span></div>
              <p style={{fontSize:'0.8rem',color:'var(--ink-400)',marginBottom:16}}>Fixed, transparent rate</p>
              {user?.role === 'parent'
                ? <button className="btn btn-primary btn-full btn-lg" onClick={()=>setShowBooking(true)}>Book a lesson</button>
                : <Link to={"/register/parent?redirect=" + encodeURIComponent(window.location.pathname)} className="btn btn-primary btn-full btn-lg">Sign up to book</Link>
              }
              <div style={{marginTop:16,display:'flex',flexDirection:'column',gap:8}}>
                <div style={{display:'flex',gap:8,fontSize:'0.8rem',color:'var(--ink-500)'}}>
                  <span style={{color:'var(--green-500)'}}>&#10003;</span>Manually vetted by our team
                </div>
                {tutor.has_dbs && (
                  <div style={{display:'flex',gap:8,fontSize:'0.8rem',color:'var(--ink-500)'}}>
                    <span style={{color:'var(--green-500)'}}>&#10003;</span>DBS checked
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showBooking && user?.role==='parent' && (
        <BookingModal tutor={tutor} user={user} onClose={()=>setShowBooking(false)}/>
      )}

      <Footer />
    </div>
  );
}
